// Home screen -- surfaces:
//   - signed-in account header + log-out (US-AUTH-01, US-AUTH-05)
//   - guardian-only "Add child" action (US-SETUP-02)
//   - buckets list for the signed-in user (US-BUCKET-01):
//       * children see their own three buckets
//       * guardians see every linked child's buckets, grouped by child

import {
  type AccountListEntry,
  type BucketEntry,
  type BucketInterestAccrualEntry,
  type ChildApprovalNotice,
  type ChoreEntry,
  type HistoryEntry,
  type RoutineInstanceEntry,
  type SavingsGoal,
  accountsList,
  bucketInterestAccrualsList,
  bucketsList,
  childApprovalNoticesList,
  choreClaim,
  choresList,
  choresRejectedCount,
  familyChallengeCurrent,
  giftMyPendingList,
  giftPendingListForGuardian,
  goalsList,
  historyList,
  myPendingDepositsList,
  myPendingTransfersList,
  myPendingWithdrawalsList,
  pendingChoresList,
  pendingDepositsList,
  pendingTransfersList,
  pendingWithdrawalsList,
  routineHistory,
  routineInstancesToday,
  routinePendingApprovals,
  routineResubmit,
  routinesGenerateToday,
  routinesListForChild,
} from '@chorepop/core/client';
import {
  type Component,
  For,
  Match,
  Show,
  Switch,
  createEffect,
  createMemo,
  createResource,
  createSignal,
  onCleanup,
  onMount,
} from 'solid-js';
import { playRewardSound, primeCoinJingleAudio } from '../audio/rewardJingle';
import { AppIcon } from '../components/AppIcon';
import { ReportContentButton } from '../components/ReportContentButton';
import { TransientBanner } from '../components/TransientBanner';
import { burstChildConfetti } from '../lib/childCelebration';
import { formatFamilyChallengeDateRange } from '../lib/familyChallengePresentation';
import {
  coinJinglesEnabled,
  confettiBurstsEnabled,
  moneySound,
} from '../state/childCelebrationPreferences';
import { isFamilyContentHidden } from '../state/familyContentVisibility';

export const REWARD_CONFIRMATION_DURATION_MS = 10_000;
import { ChildStreakPathCard } from './ChildStreakPathCard';
import { GuardianChildCard } from './GuardianChildCard';

type Props = {
  account: AccountListEntry;
  sessionId: string;
  /** Child-tab request persisted by App across a route remount. */
  requestedChildHomeView?: ChildHomeView | undefined;
  /** Optional toast banner for actions just completed elsewhere. */
  flashMessage?: string | undefined;
  onFlashDismiss?: (() => void) | undefined;
  onLogOut: () => void;
  onAddChild: () => void;
  onResetChildPassword: (childAccountId: number, childDisplayName: string) => void;
  /** Called after a successful deactivate -- caller is expected to show a flash + refetch. */
  onChildDeactivated: (childDisplayName: string) => void;
  onRecordDeposit: (childAccountId: number, childDisplayName: string) => void;
  /** US-GIFT-01 -- guardian records a gift for a child. */
  onRecordGift: (childAccountId: number, childDisplayName: string) => void;
  /** Children only: spend from one of their own buckets. */
  onSpend: (bucket: BucketEntry) => void;
  /** Children only: request a withdrawal from an approval-required bucket. */
  onRequestWithdrawal: (bucket: BucketEntry) => void;
  /** US-BUCKET-09 -- children only: open the Move Money screen with all
   *  active buckets for this child as both source and target options. */
  onMoveMoney: (childAccountId: number, buckets: BucketEntry[], currencyCode: string) => void;
  /** Shared: view the history for a given child. */
  onViewHistory: (childAccountId: number, childDisplayName: string) => void;
  /** Shared: view the chores list for a given child. */
  onViewChores: (childAccountId: number, childDisplayName: string) => void;
  /** Child only: view + cancel my own pending deposits/withdrawals. */
  onViewMyRequests: () => void;
  /** Child only: allocate a pending gift. */
  onAllocateGift: (depositId: number) => void;
  /** Guardian only: open the full pending-approvals queue (global bell). */
  onViewPendingApprovals: () => void;
  /**
   * US-UI-APPROVALS -- guardian only: open the pending-approvals queue
   * pre-filtered to a single child.
   */
  onViewPendingApprovalsForChild: (childAccountId: number) => void;
  /** Guardian only: open the Settings panel (BUG-019 -- was previously
   *  only reachable through the unauthenticated picker footer; logged-in
   *  guardians had no entry point). */
  onOpenSettings: () => void;
  /** US-HISTORY-05 -- guardian-only audit log entry. */
  onOpenAuditLog: () => void;
  /** Guardian only: open the "add guardian" screen. */
  onAddGuardian: () => void;
  /** Guardian only: view the full cross-child chore pool (master list). */
  onViewAllChores?: () => void;
  /** Guardian only: create a master-list chore any child can pick up. */
  onAddChorePool: () => void;
  /** Child only: open a routine task instance to complete it. */
  onOpenRoutineTask?: ((instanceId: number) => void) | undefined;
  /** Guardian only: open routine management screen. */
  onManageRoutines?: (() => void) | undefined;
  /** Guardian only: open recurring allowance management. */
  onManageAllowances?: (() => void) | undefined;
  /** Child only: open savings goals. */
  onOpenGoals?: (() => void) | undefined;
  /** Child only: open the dedicated giving-goals view. */
  onOpenGivingGoals?: (() => void) | undefined;
  /** Child only: open the dated routine streak history. */
  onOpenStreaks?: ((childAccountId: number, childDisplayName: string) => void) | undefined;
  /** Guardian only: open the unified BucketManagerScreen for a child. */
  onManageBuckets: (
    childAccountId: number,
    childDisplayName: string,
    buckets: BucketEntry[],
  ) => void;
  /** Guardian only: open the "deactivate bucket" screen for a guardian-managed bucket. */
  onDeactivateBucket: (bucket: BucketEntry, existingBuckets: BucketEntry[]) => void;
  /** US-BUCKET-12 -- child-only delete-with-transfer-target flow. */
  onDeleteBucket: (bucket: BucketEntry, existingBuckets: BucketEntry[]) => void;
  /** US-BUCKET-15 -- guardian directly adjusts a bucket's balance. */
  onAdjustBucketBalance: (bucket: BucketEntry) => void;
  /** US-BUCKET-16 -- guardian instant transfer between two buckets. */
  onDirectTransfer: (
    childAccountId: number,
    childDisplayName: string,
    buckets: BucketEntry[],
  ) => void;
  /** Optional -- when provided, the child name heading is clickable and opens their detail screen. */
  onViewChildDetail?: (childAccountId: number, childDisplayName: string) => void;
};

type ChildHomeView = 'chores' | 'money' | 'activity';
type RewardAnimationPhase = 'idle' | 'positioning' | 'falling' | 'settled';

type ChildDashboardData = {
  chores: ChoreEntry[];
  goals: SavingsGoal[];
  history: HistoryEntry[];
  pendingDepositCount: number;
  pendingWithdrawalCount: number;
  pendingTransferCount: number;
};

type ChildStreakPath = {
  completedDays: number;
};

/**
 * Compute a whole-number percentage for each bucket using priority-aware
 * largest-remainder rounding.  Higher priority = receives the ceiling value
 * when the pool does not divide evenly.  All returned values are integers
 * and sum to the total of all allocation_bps ÷ 100 (normally 100).
 */
function calcDisplayPercents(
  buckets: Array<{ bucketId: number; allocationBps: number; priority: number }>,
): Record<number, number> {
  if (buckets.length === 0) return {};
  const totalBps = buckets.reduce((s, b) => s + b.allocationBps, 0);
  const targetPct = Math.round(totalBps / 100);
  // Floor each bucket to whole percent
  const sorted = [...buckets].sort((a, b) => b.priority - a.priority || a.bucketId - b.bucketId);
  const floors = sorted.map((b) => Math.floor(b.allocationBps / 100));
  const floorSum = floors.reduce((s, f) => s + f, 0);
  const remainder = targetPct - floorSum;
  const result: Record<number, number> = {};
  sorted.forEach((b, i) => {
    result[b.bucketId] = (floors[i] ?? 0) + (i < remainder ? 1 : 0);
  });
  return result;
}

function approvalNoticeStorageKey(accountId: number): string {
  return `chorepop:acknowledged-approval-notices:${accountId}`;
}

function approvalNoticeFingerprint(notice: ChildApprovalNotice): string {
  return [
    notice.childApprovalNoticeId,
    notice.noticeType,
    notice.sourceEntityId,
    notice.amountMinorUnits,
    notice.currencyCode,
    notice.approvedAtUtc,
  ].join(':');
}

function readAcknowledgedApprovalNotices(accountId: number): Set<string> {
  try {
    const raw = window.localStorage.getItem(approvalNoticeStorageKey(accountId));
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    // Older builds persisted only the local numeric row ID. Those IDs can be
    // reused after a restore/database recreation, so carrying them forward can
    // incorrectly suppress a genuinely new reward animation.
    return new Set(
      parsed.filter((fingerprint): fingerprint is string => typeof fingerprint === 'string'),
    );
  } catch {
    return new Set();
  }
}

export const HomeScreen: Component<Props> = (props) => {
  const isGuardian = () => props.account.roleCode === 'guardian';

  const [bucketsResource, { refetch: refetchBuckets }] = createResource(async () => {
    const result = await bucketsList({ sessionId: props.sessionId });
    if (!result.ok) throw new Error(result.error.message);
    return result.value;
  });

  // US-HOME-01: family overview rail needs the full account list to
  // render the Guardians section alongside Kids. Fetched once on mount;
  // App.tsx invalidates by re-rendering Home after add-guardian flows
  // complete (same pattern as bucketsResource).
  const [accountsResource] = createResource(async () => {
    const result = await accountsList();
    if (!result.ok) return [];
    return result.value;
  });
  const guardians = () => (accountsResource() ?? []).filter((a) => a.roleCode === 'guardian');
  const [familyChallenge, { refetch: refetchFamilyChallenge }] = createResource(
    () => props.sessionId,
    (sessionId) =>
      familyChallengeCurrent(sessionId).then((result) => (result.ok ? result.value : null)),
  );

  // US-UI-APPROVALS -- fetch pending counts for the bell badge + per-
  // child Approvals buttons. Three lists in parallel, same pattern as
  // PendingApprovalsScreen. Counts are computed client-side from the
  // returned arrays; each entry has `childAccountId` so the per-child
  // grouping is uniform across kinds (deposits, chore completions,
  // withdrawals). Live updates: HomeScreen unmounts when the user
  // navigates to the queue; on return it remounts and refetches, so
  // counts reflect any approve/reject that happened in between.
  // Guardian-only -- children would get Unauthorized from the list
  // endpoints, so we gate the fetch on role.
  const [pendingResource] = createResource(
    () => isGuardian(),
    async (guardian) => {
      if (!guardian) return { total: 0, perChild: new Map<number, number>() };
      const [d, c, w, t, g, r] = await Promise.all([
        pendingDepositsList({ sessionId: props.sessionId }),
        pendingChoresList({ sessionId: props.sessionId }),
        pendingWithdrawalsList({ sessionId: props.sessionId }),
        pendingTransfersList({ sessionId: props.sessionId }),
        giftPendingListForGuardian(props.sessionId),
        routinePendingApprovals(props.sessionId),
      ]);
      const childIds: number[] = [];
      if (d.ok) for (const e of d.value) childIds.push(e.childAccountId);
      if (c.ok) for (const e of c.value) childIds.push(e.childAccountId);
      if (w.ok) for (const e of w.value) childIds.push(e.childAccountId);
      if (t.ok) for (const e of t.value) childIds.push(e.childAccountId);
      if (r.ok)
        for (const e of r.value) {
          if (typeof e.childAccountId === 'number') childIds.push(e.childAccountId);
        }
      // Gift deposits pending child allocation -- count toward the child's badge
      // so the parent can see that a gift is waiting to be allocated.
      if (g.ok) for (const e of g.value) childIds.push(e.childAccountId);
      const perChild = new Map<number, number>();
      for (const id of childIds) perChild.set(id, (perChild.get(id) ?? 0) + 1);
      return { total: childIds.length, perChild };
    },
  );
  const pendingFor = (childId: number) => pendingResource()?.perChild.get(childId) ?? 0;
  const pendingTotal = () => pendingResource()?.total ?? 0;

  const [acknowledgedApprovalNotices, setAcknowledgedApprovalNotices] = createSignal(
    readAcknowledgedApprovalNotices(props.account.accountId),
  );
  const [approvalNoticesResource, { refetch: refetchApprovalNotices }] = createResource(
    () => !isGuardian(),
    async (isChild) => {
      if (!isChild) return [] as ChildApprovalNotice[];
      const result = await childApprovalNoticesList(props.sessionId);
      return result.ok ? result.value : [];
    },
  );
  const unacknowledgedApprovalNotices = createMemo(() =>
    (approvalNoticesResource() ?? []).filter(
      (notice) => !acknowledgedApprovalNotices().has(approvalNoticeFingerprint(notice)),
    ),
  );
  const acknowledgeApprovalNotices = () => {
    const next = new Set(acknowledgedApprovalNotices());
    for (const notice of unacknowledgedApprovalNotices()) {
      next.add(approvalNoticeFingerprint(notice));
    }
    setAcknowledgedApprovalNotices(next);
    try {
      window.localStorage.setItem(
        approvalNoticeStorageKey(props.account.accountId),
        JSON.stringify([...next]),
      );
    } catch {
      // The banner still dismisses for this session if storage is unavailable.
    }
  };

  createEffect(() => {
    if (isGuardian() || unacknowledgedApprovalNotices().length === 0) return;
    // Keep the explicit OK control for children who finish reading early, but
    // do not leave an old money-update card pinned forever. Ten seconds is the
    // shared reading period for non-error confirmation banners.
    const timer = window.setTimeout(acknowledgeApprovalNotices, REWARD_CONFIRMATION_DURATION_MS);
    onCleanup(() => window.clearTimeout(timer));
  });

  const [childHomeView, setChildHomeView] = createSignal<ChildHomeView>(
    props.requestedChildHomeView ?? 'chores',
  );
  const [childLayoutReady, setChildLayoutReady] = createSignal(false);
  const [childCompactLayout, setChildCompactLayout] = createSignal(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = createSignal(false);
  const [rewardAnimationPhase, setRewardAnimationPhase] =
    createSignal<RewardAnimationPhase>('idle');
  const [rewardAnimationAmount, setRewardAnimationAmount] = createSignal(0);
  const animatedApprovalNoticeFingerprints = new Set<string>();
  const rewardAnimationTimers: number[] = [];
  let childRewardTarget: HTMLElement | undefined;
  let childDashboardScroll: HTMLDivElement | undefined;

  onMount(() => {
    const handleMobileTab = (event: Event) => {
      if (isGuardian()) return;
      const tab = (event as CustomEvent<string>).detail;
      if (tab === 'money' || tab === 'activity' || tab === 'chores') {
        setChildHomeView(tab);
      }
    };
    window.addEventListener('chorepop:child-home-tab', handleMobileTab);
    onCleanup(() => window.removeEventListener('chorepop:child-home-tab', handleMobileTab));
  });

  createEffect(() => {
    const requested = props.requestedChildHomeView;
    if (!isGuardian() && requested) setChildHomeView(requested);
  });

  let previousChildHomeView = childHomeView();
  createEffect(() => {
    const nextView = childHomeView();
    if (nextView === previousChildHomeView) return;
    previousChildHomeView = nextView;
    queueMicrotask(() => {
      if (childHomeView() !== nextView || !childDashboardScroll) return;
      childDashboardScroll.scrollTop = 0;
      childDashboardScroll.scrollLeft = 0;
    });
  });

  const clearRewardAnimationTimers = () => {
    for (const timer of rewardAnimationTimers.splice(0)) window.clearTimeout(timer);
  };

  onMount(() => {
    const compactQuery = window.matchMedia('(max-width: 767px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateLayoutPreferences = () => {
      setChildCompactLayout(compactQuery.matches);
      setPrefersReducedMotion(reducedMotionQuery.matches);
    };

    updateLayoutPreferences();
    setChildLayoutReady(true);
    compactQuery.addEventListener('change', updateLayoutPreferences);
    reducedMotionQuery.addEventListener('change', updateLayoutPreferences);
    onCleanup(() => {
      compactQuery.removeEventListener('change', updateLayoutPreferences);
      reducedMotionQuery.removeEventListener('change', updateLayoutPreferences);
    });
  });

  // Unlock Web Audio after the child's first interaction. Mobile webviews
  // commonly block sound created by a background sync until a user gesture
  // has occurred; the visual reward still works if audio remains blocked.
  onMount(() => {
    if (isGuardian()) return;
    const primeAudio = () => primeCoinJingleAudio();
    window.addEventListener('pointerdown', primeAudio, { passive: true });
    window.addEventListener('keydown', primeAudio);
    onCleanup(() => {
      window.removeEventListener('pointerdown', primeAudio);
      window.removeEventListener('keydown', primeAudio);
    });
  });

  onCleanup(clearRewardAnimationTimers);
  const [childDashboardResource, { refetch: refetchChildDashboard }] = createResource(
    () => !isGuardian(),
    async (isChild): Promise<ChildDashboardData> => {
      if (!isChild) {
        return {
          chores: [],
          goals: [],
          history: [],
          pendingDepositCount: 0,
          pendingWithdrawalCount: 0,
          pendingTransferCount: 0,
        };
      }

      const [chores, goals, history, deposits, withdrawals, transfers] = await Promise.all([
        choresList({ sessionId: props.sessionId, childAccountId: props.account.accountId }),
        goalsList(props.sessionId),
        historyList({
          sessionId: props.sessionId,
          childAccountId: props.account.accountId,
          limit: 8,
        }),
        myPendingDepositsList({ sessionId: props.sessionId }),
        myPendingWithdrawalsList({ sessionId: props.sessionId }),
        myPendingTransfersList({ sessionId: props.sessionId }),
      ]);

      return {
        chores: chores.ok ? chores.value : [],
        goals: goals.ok ? goals.value : [],
        history: history.ok ? history.value : [],
        pendingDepositCount: deposits.ok ? deposits.value.length : 0,
        pendingWithdrawalCount: withdrawals.ok ? withdrawals.value.length : 0,
        pendingTransferCount: transfers.ok ? transfers.value.length : 0,
      };
    },
  );

  // A path advances once for each calendar day with an approved routine. A
  // missed day simply contributes no new step, so a child keeps all the
  // progress they have already earned instead of being punished with a reset.
  const [childStreakPath, { refetch: refetchChildStreakPath }] = createResource(
    () => !isGuardian(),
    async (isChild): Promise<ChildStreakPath> => {
      if (!isChild) return { completedDays: 0 };
      const routines = await routinesListForChild(props.sessionId);
      if (!routines.ok) return { completedDays: 0 };
      const histories = await Promise.all(
        routines.value.map((routine) =>
          routineHistory(props.sessionId, routine.recurringTaskId, 365),
        ),
      );
      const approvedDays = new Set<string>();
      for (const history of histories) {
        if (!history.ok) continue;
        for (const instance of history.value) {
          if (instance.status === 'approved') approvedDays.add(instance.scheduledDate);
        }
      }
      const completedDays = approvedDays.size;
      return { completedDays };
    },
  );

  // US-GIFT-02 -- pending gift notification for children.
  // Re-fetch every time the child navigates to their home screen so a gift
  // recorded while they were on another screen (or on another device) appears
  // immediately without requiring a full app restart.
  // Throws on error so the error boundary / Show block can display a message
  // rather than silently showing nothing.
  const [pendingGiftsResource, { refetch: refetchPendingGifts }] = createResource(
    () => !isGuardian(),
    async (isChild) => {
      if (!isChild) return [];
      const r = await giftMyPendingList(props.sessionId);
      if (!r.ok) throw new Error(r.error?.message ?? 'Failed to load pending gifts');
      return r.value;
    },
  );

  onMount(() => {
    // Cloud sync is authoritative now; refresh local resources only after the
    // cloud layer reports a completed sync. There is no manual/local join
    // action in the signed-in screens.
    const handleSync = () => {
      refetchBuckets();
      refetchFamilyChallenge();
      if (!isGuardian()) {
        refetchPendingGifts();
        refetchApprovalNotices();
        refetchChildDashboard();
        refetchChildStreakPath();
      }
    };
    window.addEventListener('chorepop:sync-applied', handleSync);
    onCleanup(() => window.removeEventListener('chorepop:sync-applied', handleSync));
  });
  const pendingGiftCount = () => pendingGiftsResource()?.length ?? 0;
  const [giftsExpanded, setGiftsExpanded] = createSignal(true);

  // US-UI-CHORE-REJECTIONS -- child-side badge on the "My chores"
  // button. Returns 0 for guardians (the backend command also returns
  // 0 in that case as defence in depth). Cleared implicitly when the
  // child re-claims a rejected chore (the next mount of Home picks
  // up the new count from the resource refetch).
  const [rejectedChoresResource] = createResource(
    () => !isGuardian(),
    async (isChild) => {
      if (!isChild) return 0;
      const r = await choresRejectedCount({ sessionId: props.sessionId });
      if (!r.ok) return 0;
      return r.value;
    },
  );
  const rejectedChoresCount = () => rejectedChoresResource() ?? 0;

  // Deactivate-confirmation dialog state.
  // Group buckets by child for the guardian view.
  const grouped = createMemo<Map<number, BucketEntry[]>>(() => {
    const map = new Map<number, BucketEntry[]>();
    for (const b of bucketsResource() ?? []) {
      const arr = map.get(b.childAccountId) ?? [];
      arr.push(b);
      map.set(b.childAccountId, arr);
    }
    return map;
  });

  // Total balance across all buckets -- shown in child view header.
  const childTotalBalance = createMemo(() =>
    (bucketsResource() ?? []).reduce((sum, b) => sum + b.balanceMinorUnits, 0),
  );
  const childBucketPercents = createMemo(() => calcDisplayPercents(bucketsResource() ?? []));
  const childChores = createMemo(() => childDashboardResource()?.chores ?? []);
  const childReadyChores = createMemo(() =>
    childChores().filter((chore) => !chore.hasOpenClaim && chore.lastRejectedAtUtc == null),
  );
  const childWaitingChores = createMemo(() => childChores().filter((chore) => chore.hasOpenClaim));
  const childRejectedChores = createMemo(() =>
    childChores().filter((chore) => chore.lastRejectedAtUtc != null),
  );
  const [routineProgress, setRoutineProgress] = createSignal({ completed: 0, total: 0 });
  const childTodayCompleted = createMemo(
    () => childChores().filter((chore) => chore.hasOpenClaim).length + routineProgress().completed,
  );
  const childTodayTotal = createMemo(() => childChores().length + routineProgress().total);
  const childAvailableReward = createMemo(() =>
    childReadyChores().reduce((sum, chore) => sum + chore.rewardMinorUnits, 0),
  );
  const childSavingsGoals = createMemo(() =>
    (childDashboardResource()?.goals ?? []).filter((goal) => goal.goalType === 'saving'),
  );
  const childGivingGoals = createMemo(() =>
    (childDashboardResource()?.goals ?? []).filter((goal) => goal.goalType === 'giving'),
  );
  const primarySavingsGoal = createMemo(() => childSavingsGoals()[0] ?? null);
  const primaryGivingGoal = createMemo(() => childGivingGoals()[0] ?? null);
  const openGivingGoals = () => {
    if (props.onOpenGivingGoals) props.onOpenGivingGoals();
    else props.onOpenGoals?.();
  };
  const childPendingMoneyCount = createMemo(() => {
    const data = childDashboardResource();
    if (!data) return 0;
    return data.pendingDepositCount + data.pendingWithdrawalCount + data.pendingTransferCount;
  });
  const childWaitingTotal = createMemo(
    () => childWaitingChores().length + childRejectedChores().length + childPendingMoneyCount(),
  );
  const [quickClaimingChoreId, setQuickClaimingChoreId] = createSignal<number | null>(null);
  const [quickClaimError, setQuickClaimError] = createSignal<string | null>(null);
  const [quickClaimMessage, setQuickClaimMessage] = createSignal<string | null>(null);

  /**
   * The reward on a Today's chores row is deliberately a quick-complete
   * control.  It must submit the same claim that the full chore list does,
   * rather than merely navigating there; otherwise a child has to discover a
   * second, hidden step before their parent can review the work.
   */
  const claimTodayChore = async (chore: ChoreEntry, origin?: DOMRectReadOnly) => {
    setQuickClaimingChoreId(chore.choreId);
    setQuickClaimError(null);
    setQuickClaimMessage(null);
    try {
      const result = await choreClaim({ sessionId: props.sessionId, choreId: chore.choreId });
      if (!result.ok) {
        setQuickClaimError(
          result.error.kind === 'conflict'
            ? "You've already marked this one done — wait for your guardian."
            : result.error.message,
        );
        return;
      }
      setQuickClaimMessage(`Nice work! ${chore.choreName} is waiting for your parent.`);
      if (confettiBurstsEnabled()) burstChildConfetti(origin);
      void refetchChildDashboard();
    } finally {
      setQuickClaimingChoreId(null);
    }
  };

  createEffect(() => {
    if (
      isGuardian() ||
      !childLayoutReady() ||
      rewardAnimationPhase() !== 'idle' ||
      (bucketsResource()?.length ?? 0) === 0
    ) {
      return;
    }

    const newDepositNotices = unacknowledgedApprovalNotices().filter(
      (notice) =>
        notice.noticeType === 'deposit' &&
        !animatedApprovalNoticeFingerprints.has(approvalNoticeFingerprint(notice)),
    );
    if (newDepositNotices.length === 0) return;

    for (const notice of newDepositNotices) {
      animatedApprovalNoticeFingerprints.add(approvalNoticeFingerprint(notice));
    }
    setRewardAnimationAmount(
      newDepositNotices.reduce((total, notice) => total + notice.amountMinorUnits, 0),
    );
    setRewardAnimationPhase('positioning');

    const compact = childCompactLayout();
    if (compact) {
      setChildHomeView('money');
      window.dispatchEvent(new CustomEvent('chorepop:child-home-tab', { detail: 'money' }));
    }

    if (compact) {
      rewardAnimationTimers.push(
        window.setTimeout(() => {
          childRewardTarget?.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'start',
          });
        }, 60),
      );
    }

    if (prefersReducedMotion()) {
      rewardAnimationTimers.push(
        window.setTimeout(
          () => {
            setRewardAnimationPhase('settled');
            if (coinJinglesEnabled()) playRewardSound(moneySound());
          },
          compact ? 160 : 40,
        ),
        window.setTimeout(() => setRewardAnimationPhase('idle'), REWARD_CONFIRMATION_DURATION_MS),
      );
      return;
    }

    rewardAnimationTimers.push(
      window.setTimeout(
        () => {
          setRewardAnimationPhase('falling');
          if (coinJinglesEnabled()) playRewardSound(moneySound());
        },
        compact ? 520 : 100,
      ),
      window.setTimeout(() => setRewardAnimationPhase('settled'), compact ? 1980 : 1660),
      window.setTimeout(() => setRewardAnimationPhase('idle'), REWARD_CONFIRMATION_DURATION_MS),
    );
  });

  return (
    <main class="home-screen" classList={{ 'home-screen--child': !isGuardian() }}>
      <Show when={isGuardian()}>
        <header class="home-header">
          <div>
            <span class="home-greeting">Signed in as</span>
            <h1>{props.account.displayName}</h1>
            <Show when={!isGuardian()}>
              <span class="home-child-total num">
                {formatBalance(childTotalBalance(), props.account.displayCurrencyCode)}
              </span>
            </Show>
            <span class={`home-role home-role--${props.account.roleCode}`}>
              {isGuardian() ? 'Parent / Guardian' : 'Child'}
            </span>
          </div>
          <div class="home-header-actions">
            <Show when={isGuardian()}>
              <button
                type="button"
                class="approvals-bell"
                onClick={() => props.onViewPendingApprovals()}
                data-testid="home-approvals-bell"
                aria-label={
                  pendingTotal() > 0
                    ? `Open pending approvals (${pendingTotal()} pending)`
                    : 'Open pending approvals'
                }
              >
                <AppIcon name="bell" class="approvals-bell-glyph" />
                <Show when={pendingTotal() > 0}>
                  <span class="approvals-bell-badge" data-testid="home-approvals-bell-badge">
                    {pendingTotal()}
                  </span>
                </Show>
              </button>
            </Show>
            {/* Settings -- available to all signed-in users. */}
            <button
              type="button"
              class="ghost"
              onClick={() => props.onOpenSettings()}
              data-testid="home-open-settings"
            >
              Settings
            </button>
            {/* Activity history is the guardian's one complete family
              timeline: money entries, chores, account events and logins. */}
            <Show when={isGuardian()}>
              <button
                type="button"
                class="ghost"
                onClick={() => props.onOpenAuditLog()}
                data-testid="home-open-activity-history"
              >
                Activity history
              </button>
            </Show>
            <button type="button" class="ghost" onClick={() => props.onLogOut()}>
              Log out
            </button>
          </div>
        </header>
      </Show>

      <Show when={props.flashMessage}>
        {(message) => (
          <TransientBanner
            class="home-flash"
            message={message()}
            onDismiss={props.onFlashDismiss}
          />
        )}
      </Show>
      <Switch>
        <Match when={bucketsResource.loading}>
          <section class="home-card">
            <p>Loading buckets…</p>
          </section>
        </Match>

        <Match when={bucketsResource.error}>
          <section class="home-card">
            <p class="form-error" role="alert">
              Couldn't load buckets: {(bucketsResource.error as Error).message}
            </p>
          </section>
        </Match>

        <Match when={!isGuardian() && bucketsResource()}>
          <section
            class="child-dashboard"
            classList={{ 'child-dashboard--rewarding': rewardAnimationPhase() !== 'idle' }}
            data-reward-phase={rewardAnimationPhase()}
            data-testid="child-dashboard"
          >
            <header class="child-dashboard-welcome" data-testid="child-dashboard-header">
              <span class="child-dashboard-avatar" aria-hidden="true">
                {props.account.displayName.slice(0, 1).toUpperCase()}
              </span>
              <div>
                <div class="child-dashboard-kicker">CHILD ACCOUNT</div>
                <h1>Hi, {props.account.displayName}!</h1>
                <p>Choose a chore to get started.</p>
              </div>
            </header>

            <div
              ref={childDashboardScroll}
              class="child-dashboard-scroll"
              data-testid="child-dashboard-scroll"
            >
              <Show when={unacknowledgedApprovalNotices().length > 0}>
                <section
                  class="child-dashboard-card child-approval-notice"
                  role="status"
                  data-testid="child-approval-notice"
                >
                  <div class="child-dashboard-section-heading">
                    <div>
                      <div class="child-dashboard-kicker">WHILE YOU WERE AWAY</div>
                      <h2>Money updates from your parent/guardian</h2>
                    </div>
                    <span class="history-chip">{unacknowledgedApprovalNotices().length}</span>
                  </div>
                  <div class="child-dashboard-list child-approval-notice-list">
                    <For each={unacknowledgedApprovalNotices()}>
                      {(notice) => (
                        <div
                          class="child-dashboard-row"
                          data-testid={`child-approval-notice-${notice.childApprovalNoticeId}`}
                        >
                          <div>
                            <strong>
                              {notice.noticeType === 'deposit'
                                ? 'Deposit approved'
                                : 'Withdrawal approved'}
                            </strong>
                            <span class="child-dashboard-muted">From your parent/guardian</span>
                          </div>
                          <strong class="num">
                            {notice.noticeType === 'deposit' ? '+' : '−'}
                            {formatBalance(notice.amountMinorUnits, notice.currencyCode)}
                          </strong>
                        </div>
                      )}
                    </For>
                  </div>
                  <button
                    type="button"
                    class="primary"
                    onClick={acknowledgeApprovalNotices}
                    data-testid="child-approval-notice-ok"
                  >
                    OK
                  </button>
                </section>
              </Show>

              <Show when={childHomeView() === 'chores'}>
                <section class="child-progress-card" data-testid="child-progress-card">
                  <div class="child-progress-ring" aria-hidden="true">
                    <svg viewBox="0 0 56 56" role="presentation">
                      <circle
                        class="child-progress-ring-track"
                        cx="28"
                        cy="28"
                        r="23"
                        pathLength="100"
                      />
                      <circle
                        class="child-progress-ring-value"
                        cx="28"
                        cy="28"
                        r="23"
                        pathLength="100"
                        style={`stroke-dasharray: ${Math.max(0, Math.min(100, (childTodayCompleted() / Math.max(childTodayTotal(), 1)) * 100))} 100`}
                      />
                    </svg>
                    <strong
                      data-testid="child-today-progress-count"
                      aria-label={`${childTodayCompleted()} of ${childTodayTotal()} jobs and routines complete today`}
                    >
                      {childTodayCompleted()}/{childTodayTotal()}
                    </strong>
                  </div>
                  <div class="child-progress-copy">
                    <div class="child-dashboard-kicker">TODAY'S PROGRESS</div>
                    <h2>Small jobs make a big difference.</h2>
                    <p>
                      {childAvailableReward() > 0
                        ? `${formatBalance(childAvailableReward(), props.account.displayCurrencyCode)} still to earn today.`
                        : 'You are all caught up for today!'}
                    </p>
                  </div>
                </section>
                <Show when={!childStreakPath.loading}>
                  <ChildStreakPathCard
                    completedDays={childStreakPath()?.completedDays ?? 0}
                    onOpen={() =>
                      props.onOpenStreaks?.(props.account.accountId, props.account.displayName)
                    }
                  />
                </Show>
                <Show when={familyChallenge()}>
                  {(challenge) => {
                    const myProgress = () =>
                      challenge().children.find(
                        (child) => child.childAccountId === props.account.accountId,
                      )?.completedDays ?? 0;
                    const familyComplete = () =>
                      challenge().children.length > 0 &&
                      challenge().children.every(
                        (child) => child.completedDays >= challenge().targetDaysPerChild,
                      );
                    const childComplete = () => myProgress() >= challenge().targetDaysPerChild;
                    return (
                      <section
                        class="child-streak-path child-family-challenge"
                        data-testid="child-family-challenge"
                      >
                        <div>
                          <div class="child-dashboard-kicker">FAMILY CHALLENGE</div>
                          <h2>{challenge().challengeName}</h2>
                          <p>
                            {familyComplete()
                              ? `Challenge complete! Every child reached ${challenge().targetDaysPerChild} of ${challenge().targetDaysPerChild} days.`
                              : childComplete()
                                ? `You reached ${challenge().targetDaysPerChild} of ${challenge().targetDaysPerChild} days. Your part is done. The family finishes when every child reaches their goal.`
                                : `You have ${Math.min(myProgress(), challenge().targetDaysPerChild)} of ${challenge().targetDaysPerChild} days. To make today count, finish every chore and routine due today and have a guardian approve them.`}
                          </p>
                          <p class="family-challenge-rule">
                            One day counts after every chore and routine due for you that day is
                            approved. A day with no jobs does not count. Your days do not need to be
                            in a row.
                          </p>
                          <p class="family-challenge-dates">
                            7-day challenge:{' '}
                            {formatFamilyChallengeDateRange(
                              challenge().startsOn,
                              challenge().endsOn,
                            )}
                          </p>
                        </div>
                        <div
                          class="child-streak-path-steps"
                          aria-label={`My family challenge progress: ${Math.min(myProgress(), challenge().targetDaysPerChild)} of ${challenge().targetDaysPerChild} days`}
                        >
                          <For
                            each={Array.from(
                              { length: challenge().targetDaysPerChild },
                              (_, index) => index,
                            )}
                          >
                            {(step) => (
                              <span
                                class="child-streak-path-step"
                                classList={{ 'is-complete': step < myProgress() }}
                                aria-hidden="true"
                              >
                                {step < myProgress() ? '★' : '•'}
                              </span>
                            )}
                          </For>
                        </div>
                      </section>
                    );
                  }}
                </Show>
                <div class="child-landscape-grid" data-testid="child-chores-dashboard">
                  <div class="child-dashboard-column child-dashboard-money-column">
                    <section
                      ref={(element) => {
                        childRewardTarget = element;
                      }}
                      class="child-dashboard-card child-reward-target"
                      data-testid="child-money-overview"
                    >
                      <div class="child-dashboard-kicker">YOUR MONEY</div>
                      <div class="child-dashboard-total num">
                        {formatBalance(childTotalBalance(), props.account.displayCurrencyCode)}
                      </div>
                      <p class="child-dashboard-muted">Your total across your own buckets</p>

                      <Show
                        when={(bucketsResource()?.length ?? 0) > 0}
                        fallback={<p>No buckets yet.</p>}
                      >
                        <div class="child-dashboard-buckets" aria-label="Bucket balances">
                          <For each={bucketsResource() ?? []}>
                            {(bucket, index) => (
                              <div
                                class="child-dashboard-bucket"
                                classList={{
                                  'is-rewarding': rewardAnimationPhase() === 'falling',
                                  'is-reward-settled': rewardAnimationPhase() === 'settled',
                                }}
                                style={
                                  bucket.displayColor
                                    ? `--bucket-color: ${bucket.displayColor}`
                                    : undefined
                                }
                                data-testid={`child-reward-bucket-${bucket.bucketId}`}
                              >
                                <Show when={rewardAnimationPhase() === 'falling'}>
                                  <span
                                    class="child-reward-coin"
                                    style={`--reward-index: ${index()}`}
                                    aria-hidden="true"
                                  >
                                    £
                                  </span>
                                </Show>
                                <strong>{bucket.bucketName}</strong>
                                <span class="child-dashboard-bucket-amount num">
                                  {formatBalance(
                                    bucket.balanceMinorUnits,
                                    props.account.displayCurrencyCode,
                                  )}
                                </span>
                                <span class="child-dashboard-muted">
                                  {childBucketPercents()[bucket.bucketId] ?? 0}% of deposits
                                </span>
                              </div>
                            )}
                          </For>
                        </div>
                        <div class="child-dashboard-split-copy">
                          {/* BUG-059: the non-breaking space makes this one short
                              label an indivisible token even when a WebView applies
                              emergency character wrapping at large text sizes. */}
                          <strong>{'Automatic\u00a0split'}</strong>
                          <span>
                            {(bucketsResource() ?? [])
                              .map(
                                (bucket) =>
                                  `${bucket.bucketName} ${childBucketPercents()[bucket.bucketId] ?? 0}%`,
                              )
                              .join(' · ')}
                          </span>
                        </div>
                        <div
                          class="child-dashboard-split-track"
                          aria-label="Automatic deposit split"
                        >
                          <For each={bucketsResource() ?? []}>
                            {(bucket) => (
                              <span
                                style={`--bucket-share: ${Math.max(bucket.allocationBps, 1)}; --bucket-color: ${
                                  bucket.displayColor ?? 'var(--color-accent)'
                                }`}
                              />
                            )}
                          </For>
                        </div>
                      </Show>
                      <Show when={rewardAnimationPhase() === 'settled'}>
                        <div class="child-reward-confirmation" role="status">
                          +
                          {formatBalance(
                            rewardAnimationAmount(),
                            props.account.displayCurrencyCode,
                          )}{' '}
                          added to your buckets
                        </div>
                      </Show>
                    </section>

                    <section class="child-dashboard-card" data-testid="child-savings-goal-card">
                      <Show
                        when={primarySavingsGoal()}
                        fallback={
                          <>
                            <div class="child-dashboard-section-heading">
                              <div>
                                <div class="child-dashboard-kicker">SAVINGS GOAL</div>
                                <h2>No goal chosen yet</h2>
                              </div>
                            </div>
                            <p class="child-dashboard-muted">
                              Choose something you would like to save towards.
                            </p>
                            <Show when={props.onOpenGoals}>
                              <button
                                type="button"
                                class="ghost"
                                onClick={() => props.onOpenGoals?.()}
                                data-testid="home-child-goals-button"
                              >
                                Choose a savings goal
                              </button>
                            </Show>
                          </>
                        }
                      >
                        {(goal) => (
                          <>
                            <div class="child-dashboard-section-heading">
                              <div class="child-dashboard-goal-title">
                                <span class="child-dashboard-goal-icon" aria-hidden="true">
                                  {goal().goalEmoji ?? '★'}
                                </span>
                                <div>
                                  <div class="child-dashboard-kicker">SAVINGS GOAL</div>
                                  <h2>{goal().goalName}</h2>
                                </div>
                              </div>
                              <Show when={childSavingsGoals().length > 1}>
                                <span class="history-chip">
                                  +{childSavingsGoals().length - 1} more
                                </span>
                              </Show>
                            </div>
                            <div class="child-dashboard-goal-value">
                              <strong>
                                {formatBalance(
                                  goal().currentMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}{' '}
                                of{' '}
                                {formatBalance(
                                  goal().targetMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}
                              </strong>
                              <span>{goal().progressPct}%</span>
                            </div>
                            <div
                              class="child-dashboard-goal-track"
                              role="img"
                              aria-label={`${goal().progressPct}% of ${goal().goalName} goal reached`}
                            >
                              <span
                                style={`width: ${Math.max(0, Math.min(100, goal().progressPct))}%`}
                              />
                            </div>
                            <Show when={props.onOpenGoals}>
                              <button
                                type="button"
                                class="ghost"
                                onClick={() => props.onOpenGoals?.()}
                                data-testid="home-child-goals-button"
                              >
                                Change savings goal
                              </button>
                            </Show>
                          </>
                        )}
                      </Show>
                    </section>

                    <section class="child-dashboard-card" data-testid="child-giving-goal-card">
                      <Show
                        when={primaryGivingGoal()}
                        fallback={
                          <>
                            <div class="child-dashboard-section-heading">
                              <div>
                                <div class="child-dashboard-kicker">GIVING GOAL</div>
                                <h2>No giving goal chosen yet</h2>
                              </div>
                            </div>
                            <p class="child-dashboard-muted">
                              Choose a cause you would like to give to.
                            </p>
                            <Show when={props.onOpenGivingGoals ?? props.onOpenGoals}>
                              <button
                                type="button"
                                class="ghost"
                                onClick={openGivingGoals}
                                data-testid="home-child-giving-goals-button"
                              >
                                Choose a giving goal
                              </button>
                            </Show>
                          </>
                        }
                      >
                        {(goal) => (
                          <>
                            <div class="child-dashboard-section-heading">
                              <div class="child-dashboard-goal-title">
                                <span class="child-dashboard-goal-icon" aria-hidden="true">
                                  {goal().goalEmoji ?? '♥'}
                                </span>
                                <div>
                                  <div class="child-dashboard-kicker">GIVING GOAL</div>
                                  <h2>{goal().goalName}</h2>
                                </div>
                              </div>
                              <Show when={childGivingGoals().length > 1}>
                                <span class="history-chip">
                                  +{childGivingGoals().length - 1} more
                                </span>
                              </Show>
                            </div>
                            <div class="child-dashboard-goal-value">
                              <strong>
                                {formatBalance(
                                  goal().currentMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}{' '}
                                of{' '}
                                {formatBalance(
                                  goal().targetMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}
                              </strong>
                              <span>{goal().progressPct}%</span>
                            </div>
                            <div
                              class="child-dashboard-goal-track"
                              role="img"
                              aria-label={`${goal().progressPct}% of ${goal().goalName} giving goal reached`}
                            >
                              <span
                                style={`width: ${Math.max(0, Math.min(100, goal().progressPct))}%`}
                              />
                            </div>
                            <Show when={props.onOpenGivingGoals ?? props.onOpenGoals}>
                              <button
                                type="button"
                                class="ghost"
                                onClick={openGivingGoals}
                                data-testid="home-child-giving-goals-button"
                              >
                                Change giving goal
                              </button>
                            </Show>
                          </>
                        )}
                      </Show>
                    </section>
                  </div>

                  <section
                    class="child-dashboard-card child-dashboard-chores-card"
                    data-testid="child-chores-card"
                  >
                    <div class="child-dashboard-section-heading">
                      <h2>Jobs for today</h2>
                      <span class="history-chip">
                        {formatBalance(childAvailableReward(), props.account.displayCurrencyCode)}{' '}
                        to earn
                      </span>
                    </div>

                    <TodaysRoutineTasks
                      sessionId={props.sessionId}
                      currency={props.account.displayCurrencyCode}
                      onOpenTask={(id) => props.onOpenRoutineTask?.(id)}
                      onProgressChange={(completed, total) =>
                        setRoutineProgress({ completed, total })
                      }
                    />

                    <Show when={quickClaimMessage()}>
                      {(message) => (
                        <TransientBanner
                          as="p"
                          class="child-dashboard-success"
                          message={message()}
                          testId="child-quick-claim-success"
                          onDismiss={() => setQuickClaimMessage(null)}
                        />
                      )}
                    </Show>
                    <Show when={quickClaimError()}>
                      {(message) => (
                        <p class="form-error" role="alert" data-testid="child-quick-claim-error">
                          {message()}
                        </p>
                      )}
                    </Show>

                    <Show when={childDashboardResource.loading}>
                      <p class="child-dashboard-muted">Loading chores…</p>
                    </Show>
                    <Show
                      when={childChores().length > 0}
                      fallback={<p class="child-dashboard-muted">No jobs for you right now.</p>}
                    >
                      <div class="child-dashboard-list">
                        <For
                          each={childChores()
                            .filter((chore) => !isFamilyContentHidden(`chore:${chore.choreId}`))
                            .slice(0, 4)}
                        >
                          {(chore) => (
                            <div
                              class="child-dashboard-row"
                              data-testid={`child-dashboard-chore-${chore.choreId}`}
                            >
                              <div>
                                <strong>{chore.choreName}</strong>
                                <span class="child-dashboard-muted">
                                  {chore.lastRejectedAtUtc != null
                                    ? 'Needs attention'
                                    : chore.hasOpenClaim
                                      ? 'Waiting for parent'
                                      : (chore.zoneName ?? chore.choreTypeLabel)}
                                </span>
                              </div>
                              <Show
                                when={!chore.hasOpenClaim && chore.lastRejectedAtUtc == null}
                                fallback={
                                  <span class="history-chip">
                                    {chore.lastRejectedAtUtc != null
                                      ? 'Review'
                                      : `${formatBalance(
                                          chore.rewardMinorUnits,
                                          props.account.displayCurrencyCode,
                                        )} waiting`}
                                  </span>
                                }
                              >
                                <button
                                  type="button"
                                  class="bucket-tile-spend"
                                  disabled={quickClaimingChoreId() === chore.choreId}
                                  onClick={(event) =>
                                    void claimTodayChore(
                                      chore,
                                      event.currentTarget.getBoundingClientRect(),
                                    )
                                  }
                                  data-testid={`child-dashboard-quick-claim-${chore.choreId}`}
                                >
                                  {quickClaimingChoreId() === chore.choreId
                                    ? 'Sending…'
                                    : formatBalance(
                                        chore.rewardMinorUnits,
                                        props.account.displayCurrencyCode,
                                      )}
                                </button>
                              </Show>
                              <ReportContentButton
                                context={{
                                  contentKind: 'chore',
                                  contentLocalId: `chore:${chore.choreId}`,
                                  reportedLocalAccountId: chore.createdByAccountId,
                                  label: `chore “${chore.choreName}”`,
                                  contentSnapshot: {
                                    choreId: chore.choreId,
                                    choreName: chore.choreName,
                                    choreDescription: chore.choreDescription,
                                    createdByAccountId: chore.createdByAccountId,
                                    childAccountId: chore.childAccountId,
                                    lastRejectionReason: chore.lastRejectionReason,
                                  },
                                }}
                              />
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>

                    <button
                      type="button"
                      class="ghost"
                      onClick={() =>
                        props.onViewChores(props.account.accountId, props.account.displayName)
                      }
                      data-testid="home-child-chores-button"
                    >
                      See all jobs
                      <Show when={rejectedChoresCount() > 0}>
                        <span class="approvals-child-badge" data-testid="home-child-chores-badge">
                          {rejectedChoresCount()}
                        </span>
                      </Show>
                    </button>
                  </section>

                  <div class="child-dashboard-column child-dashboard-updates-column">
                    <Show when={pendingGiftsResource.error}>
                      <p class="home-error">
                        Couldn't load pending gifts: {(pendingGiftsResource.error as Error).message}
                      </p>
                    </Show>
                    <Show when={pendingGiftCount() > 0}>
                      <section class="child-dashboard-card gift-pending-section">
                        <button
                          type="button"
                          class="gift-pending-heading-toggle"
                          onClick={() => setGiftsExpanded((value) => !value)}
                          aria-expanded={giftsExpanded()}
                        >
                          <span>Gifts to allocate</span>
                          <span class="gift-pending-count-badge">{pendingGiftCount()}</span>
                          <span class="gift-pending-chevron" aria-hidden="true">
                            {giftsExpanded() ? '▲' : '▼'}
                          </span>
                        </button>
                        <Show when={giftsExpanded()}>
                          <For each={pendingGiftsResource()}>
                            {(gift) => (
                              <button
                                type="button"
                                class="gift-pending-card"
                                onClick={() => props.onAllocateGift(gift.depositId)}
                                data-testid="home-gift-banner"
                              >
                                <div class="gift-pending-card-amount">
                                  {formatBalance(
                                    gift.amountMinorUnits,
                                    props.account.displayCurrencyCode,
                                  )}
                                </div>
                                <div class="gift-pending-card-body">
                                  <span class="gift-pending-card-label">
                                    {gift.note ? `"${gift.note}"` : 'A gift for you!'}
                                  </span>
                                  <span class="gift-pending-card-hint">
                                    Choose which buckets receive it
                                  </span>
                                </div>
                                <span class="gift-pending-card-cta" aria-hidden="true">
                                  →
                                </span>
                              </button>
                            )}
                          </For>
                        </Show>
                      </section>
                    </Show>

                    <section class="child-dashboard-card" data-testid="child-waiting-card">
                      <div class="child-dashboard-section-heading">
                        <h2>Waiting for parent</h2>
                        <span class="history-chip">{childWaitingTotal()}</span>
                      </div>
                      <div class="child-dashboard-list">
                        <For each={childWaitingChores().slice(0, 2)}>
                          {(chore) => (
                            <div class="child-dashboard-row">
                              <div>
                                <strong>{chore.choreName}</strong>
                                <span class="child-dashboard-muted">Chore approval</span>
                              </div>
                              <span>
                                {formatBalance(
                                  chore.rewardMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}
                              </span>
                            </div>
                          )}
                        </For>
                        <Show when={childRejectedChores().length > 0}>
                          <div class="child-dashboard-row">
                            <div>
                              <strong>Chores need attention</strong>
                              <span class="child-dashboard-muted">
                                Review and resubmit or archive
                              </span>
                            </div>
                            <span class="history-chip">{childRejectedChores().length}</span>
                          </div>
                        </Show>
                        <Show when={childPendingMoneyCount() > 0}>
                          <div class="child-dashboard-row">
                            <div>
                              <strong>Money requests</strong>
                              <span class="child-dashboard-muted">Waiting for approval</span>
                            </div>
                            <span class="history-chip">{childPendingMoneyCount()}</span>
                          </div>
                        </Show>
                        <Show when={childWaitingTotal() === 0}>
                          <p class="child-dashboard-muted">Nothing is waiting right now.</p>
                        </Show>
                      </div>
                      <button
                        type="button"
                        class="ghost"
                        onClick={() => props.onViewMyRequests()}
                        data-testid="home-child-requests-button"
                      >
                        View my requests
                      </button>
                    </section>
                  </div>
                </div>
              </Show>

              <Show when={childHomeView() === 'money'}>
                <div class="child-detail-grid" data-testid="child-money-screen">
                  <section
                    ref={(element) => {
                      childRewardTarget = element;
                    }}
                    class="child-dashboard-card child-reward-target"
                    data-testid="child-money-reward-target"
                  >
                    <div class="child-dashboard-kicker">MONEY</div>
                    <div class="child-dashboard-total num">
                      {formatBalance(childTotalBalance(), props.account.displayCurrencyCode)}
                    </div>
                    <p class="child-dashboard-muted">Your buckets and balances</p>
                    <Show
                      when={(bucketsResource()?.length ?? 0) > 0}
                      fallback={<p>No buckets yet.</p>}
                    >
                      <div
                        class="child-dashboard-buckets child-dashboard-buckets--reward"
                        aria-label="Bucket balance summary"
                      >
                        <For each={bucketsResource() ?? []}>
                          {(bucket, index) => (
                            <div
                              class="child-dashboard-bucket"
                              classList={{
                                'is-rewarding': rewardAnimationPhase() === 'falling',
                                'is-reward-settled': rewardAnimationPhase() === 'settled',
                              }}
                              style={
                                bucket.displayColor
                                  ? `--bucket-color: ${bucket.displayColor}`
                                  : undefined
                              }
                              data-testid={`child-reward-bucket-${bucket.bucketId}`}
                            >
                              <Show when={rewardAnimationPhase() === 'falling'}>
                                <span
                                  class="child-reward-coin"
                                  style={`--reward-index: ${index()}`}
                                  aria-hidden="true"
                                >
                                  £
                                </span>
                              </Show>
                              <strong>{bucket.bucketName}</strong>
                              <span class="child-dashboard-bucket-amount num">
                                {formatBalance(
                                  bucket.balanceMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}
                              </span>
                              <span class="child-dashboard-muted">
                                {childBucketPercents()[bucket.bucketId] ?? 0}% of deposits
                              </span>
                            </div>
                          )}
                        </For>
                      </div>
                      <Show when={rewardAnimationPhase() === 'settled'}>
                        <div class="child-reward-confirmation" role="status">
                          +
                          {formatBalance(
                            rewardAnimationAmount(),
                            props.account.displayCurrencyCode,
                          )}{' '}
                          added to your buckets
                        </div>
                      </Show>
                      <div class="child-dashboard-detail-heading">Spend or manage a bucket</div>
                      <BucketGrid
                        buckets={bucketsResource() ?? []}
                        currency={props.account.displayCurrencyCode}
                        sessionId={props.sessionId}
                        onSpend={(bucket) => props.onSpend(bucket)}
                        onRequestWithdrawal={(bucket) => props.onRequestWithdrawal(bucket)}
                        editPolicy="child-self"
                        onDeactivateBucket={(bucket, siblings) =>
                          props.onDeactivateBucket(bucket, siblings)
                        }
                        deactivatePolicy="child-self"
                        onDeleteBucket={(bucket, siblings) =>
                          props.onDeleteBucket(bucket, siblings)
                        }
                      />
                    </Show>
                  </section>

                  <section class="child-dashboard-card">
                    <div class="child-dashboard-section-heading">
                      <div>
                        <div class="child-dashboard-kicker">REQUESTS AND GOALS</div>
                        <h2>What would you like to do?</h2>
                      </div>
                      <Show when={childPendingMoneyCount() > 0}>
                        <span class="history-chip">{childPendingMoneyCount()} waiting</span>
                      </Show>
                    </div>

                    <div class="child-dashboard-money-actions">
                      <button
                        type="button"
                        class="ghost"
                        onClick={() => props.onViewMyRequests()}
                        data-testid="home-child-requests-button"
                      >
                        My requests
                      </button>
                      <button
                        type="button"
                        class="ghost"
                        onClick={() => {
                          const buckets = bucketsResource();
                          if (!buckets) return;
                          props.onMoveMoney(
                            props.account.accountId,
                            buckets,
                            props.account.displayCurrencyCode,
                          );
                        }}
                        disabled={(bucketsResource() ?? []).length < 2}
                        data-testid="home-child-move-money-button"
                      >
                        Move money
                      </button>
                      <Show when={props.onOpenGoals}>
                        <button
                          type="button"
                          class="ghost"
                          onClick={() => props.onOpenGoals?.()}
                          data-testid="home-child-goals-button"
                        >
                          Goals
                          <span>{childSavingsGoals().length + childGivingGoals().length}</span>
                        </button>
                      </Show>
                      <button
                        type="button"
                        class="ghost"
                        onClick={() =>
                          props.onManageBuckets(
                            props.account.accountId,
                            props.account.displayName,
                            bucketsResource() ?? [],
                          )
                        }
                        data-testid="home-child-add-bucket"
                      >
                        Edit buckets
                      </button>
                    </div>
                  </section>
                </div>
              </Show>

              <Show when={childHomeView() === 'activity'}>
                <div class="child-detail-grid" data-testid="child-activity-screen">
                  <header class="child-dashboard-card child-activity-intro">
                    <div class="child-dashboard-kicker">ME</div>
                    <h2>My activity</h2>
                  </header>
                  <section class="child-dashboard-card">
                    <div class="child-dashboard-section-heading">
                      <div>
                        <div class="child-dashboard-kicker">MONEY ACTIVITY</div>
                        <h2>Recent money changes</h2>
                      </div>
                    </div>
                    <Show
                      when={(childDashboardResource()?.history.length ?? 0) > 0}
                      fallback={<p class="child-dashboard-muted">No money activity yet.</p>}
                    >
                      <div class="child-dashboard-list">
                        <For each={childDashboardResource()?.history ?? []}>
                          {(entry) => (
                            <div class="child-dashboard-row">
                              <div>
                                <strong>
                                  {entry.kind === 'deposit'
                                    ? entry.sourceLabel
                                    : entry.kind === 'withdrawal'
                                      ? entry.reasonLabel
                                      : `Moved from ${entry.sourceBucketName}${
                                          entry.targetBucketName
                                            ? ` to ${entry.targetBucketName}`
                                            : ''
                                        }`}
                                </strong>
                                <span class="child-dashboard-muted">
                                  {formatShortDate(entry.atUtc)}
                                  {entry.kind === 'withdrawal'
                                    ? ` · ${entry.bucketName}`
                                    : entry.kind === 'transfer'
                                      ? ` · ${entry.statusLabel}`
                                      : ''}
                                </span>
                              </div>
                              <strong class="num">
                                {entry.kind === 'deposit'
                                  ? '+'
                                  : entry.kind === 'withdrawal'
                                    ? '−'
                                    : '↔'}
                                {formatBalance(
                                  entry.amountMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}
                              </strong>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                    <button
                      type="button"
                      class="ghost"
                      data-testid="home-child-history-button"
                      onClick={() =>
                        props.onViewHistory(props.account.accountId, props.account.displayName)
                      }
                    >
                      Open full money history
                    </button>
                  </section>

                  <section class="child-dashboard-card">
                    <div class="child-dashboard-section-heading">
                      <div>
                        <div class="child-dashboard-kicker">CHORE ACTIVITY</div>
                        <h2>Current chore progress</h2>
                      </div>
                    </div>
                    <Show
                      when={childChores().length > 0}
                      fallback={<p class="child-dashboard-muted">No chore activity yet.</p>}
                    >
                      <div class="child-dashboard-list">
                        <For each={childChores()}>
                          {(chore) => (
                            <div class="child-dashboard-row">
                              <div>
                                <strong>{chore.choreName}</strong>
                                <span class="child-dashboard-muted">
                                  {chore.lastRejectedAtUtc != null
                                    ? 'Needs attention'
                                    : chore.hasOpenClaim
                                      ? 'Completed · waiting for approval'
                                      : 'Ready to do'}
                                </span>
                              </div>
                              <span class="history-chip">
                                {formatBalance(
                                  chore.rewardMinorUnits,
                                  props.account.displayCurrencyCode,
                                )}
                              </span>
                            </div>
                          )}
                        </For>
                      </div>
                    </Show>
                    <button
                      type="button"
                      class="ghost"
                      onClick={() =>
                        props.onViewChores(props.account.accountId, props.account.displayName)
                      }
                    >
                      Open full chore list
                    </button>
                  </section>
                </div>
              </Show>
            </div>
          </section>
        </Match>

        <Match when={isGuardian() && bucketsResource()}>
          {/* Guardian view -- sidebar (family overview + nav) + content (per-child cards).
              Per docs/design/SPEC.md S.2 and screens/02-guardian-home.png. */}
          <div class="guardian-shell">
            {/* LEFT: family overview + navigation */}
            <aside class="guardian-rail">
              <div class="guardian-rail-section">
                <p class="guardian-rail-eyebrow">CHILDREN</p>
                <Show
                  when={grouped().size > 0}
                  fallback={<p class="guardian-rail-empty">Add a child to get started.</p>}
                >
                  <ul class="guardian-rail-children">
                    <For each={Array.from(grouped().entries())}>
                      {([childId, childBuckets]) => {
                        const childName = childBuckets[0]?.childDisplayName ?? `Child #${childId}`;
                        const total = childBuckets.reduce((s, b) => s + b.balanceMinorUnits, 0);
                        return (
                          <li>
                            <button
                              type="button"
                              class="guardian-rail-child guardian-rail-child--clickable"
                              onClick={() => props.onViewChildDetail?.(childId, childName)}
                              data-testid={`home-rail-child-${childId}`}
                              aria-label={
                                pendingFor(childId) > 0
                                  ? `${childName} -- ${pendingFor(childId)} pending`
                                  : childName
                              }
                            >
                              <span class="guardian-rail-child-avatar-wrap">
                                <span class="guardian-rail-child-avatar" aria-hidden="true">
                                  {childName.slice(0, 1).toUpperCase()}
                                </span>
                                <Show when={pendingFor(childId) > 0}>
                                  <span class="guardian-rail-child-badge" aria-hidden="true">
                                    {pendingFor(childId)}
                                  </span>
                                </Show>
                              </span>
                              <span class="guardian-rail-child-name">{childName}</span>
                              <span class="guardian-rail-child-balance num">
                                {formatBalance(total, props.account.displayCurrencyCode)}
                              </span>
                            </button>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </Show>
                {/* US-HOME-01: inline + Add a child button under the
                    children list. Visible on every render of the
                    guardian view (not gated on grouped().size) so it
                    also serves as the empty-state action when zero
                    kids exist. Uses the same .guardian-rail-add chrome
                    as the new Guardians section below. */}
                <button
                  type="button"
                  class="guardian-rail-add"
                  onClick={() => props.onAddChild()}
                  data-testid="home-inline-add-child"
                >
                  <span class="guardian-rail-add-glyph" aria-hidden="true">
                    +
                  </span>
                  <span>Add a child</span>
                </button>
              </div>

              {/* US-HOME-01: new Guardians section in the rail --
                  separated from Kids by a section heading. The current
                  signed-in guardian is shown with a small "you" badge
                  so the user can tell themselves apart at a glance.
                  Inline + Add a guardian button at the bottom of the
                  list -- guardian-only entry point that replaces the
                  bottom-of-Home Add guardian button removed by
                  US-SETUP-06. */}
              <div class="guardian-rail-section">
                <p class="guardian-rail-eyebrow">GUARDIANS</p>
                <Show
                  when={guardians().length > 0}
                  fallback={<p class="guardian-rail-empty">Loading guardians…</p>}
                >
                  <ul class="guardian-rail-children">
                    <For each={guardians()}>
                      {(g) => {
                        const isSelf = g.accountId === props.account.accountId;
                        return (
                          <li class="guardian-rail-child guardian-rail-child--guardian">
                            <span
                              class="guardian-rail-child-avatar guardian-rail-child-avatar--guardian"
                              aria-hidden="true"
                            >
                              {g.displayName.slice(0, 1).toUpperCase()}
                            </span>
                            <span class="guardian-rail-child-name">{g.displayName}</span>
                            <Show when={isSelf}>
                              <span class="guardian-rail-self-badge">you</span>
                            </Show>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </Show>
                <button
                  type="button"
                  class="guardian-rail-add"
                  onClick={() => props.onAddGuardian()}
                  data-testid="home-inline-add-guardian"
                >
                  <span class="guardian-rail-add-glyph" aria-hidden="true">
                    +
                  </span>
                  <span>Add a guardian</span>
                </button>
              </div>
            </aside>

            {/* RIGHT: content */}
            <div class="guardian-content">
              <header class="guardian-content-header">
                <p class="guardian-content-eyebrow">TODAY</p>
                <h2 class="guardian-content-title h-display">Family Overview</h2>
                <p class="guardian-content-subtitle">
                  Approve a deposit and the money splits across buckets. Reject and the kid sees
                  why.
                </p>
              </header>

              <Show
                when={grouped().size > 0}
                fallback={
                  <section class="home-card">
                    <h2>Children</h2>
                    <p>No children yet. Use "Add child" below to create one.</p>
                  </section>
                }
              >
                <For each={Array.from(grouped().entries())}>
                  {([childId, childBuckets]) => {
                    const childName = childBuckets[0]?.childDisplayName ?? `Child #${childId}`;
                    return (
                      <GuardianChildCard
                        account={props.account}
                        sessionId={props.sessionId}
                        childId={childId}
                        childName={childName}
                        childBuckets={childBuckets}
                        pendingCount={pendingFor(childId)}
                        renderBucketGrid={() => (
                          <BucketGrid
                            buckets={childBuckets}
                            currency={props.account.displayCurrencyCode}
                            sessionId={props.sessionId}
                            onDeactivateBucket={(bucket, siblings) =>
                              props.onDeactivateBucket(bucket, siblings)
                            }
                          />
                        )}
                        onChildDeactivated={(childDisplayName) => {
                          props.onChildDeactivated(childDisplayName);
                          void refetchBuckets();
                        }}
                        onViewPendingApprovalsForChild={props.onViewPendingApprovalsForChild}
                        onRecordDeposit={props.onRecordDeposit}
                        onRecordGift={props.onRecordGift}
                        onViewHistory={props.onViewHistory}
                        onViewChores={props.onViewChores}
                        onAddBucket={props.onManageBuckets}
                        onResetChildPassword={props.onResetChildPassword}
                        onAdjustBucketBalance={props.onAdjustBucketBalance}
                        onDirectTransfer={props.onDirectTransfer}
                        onManageBuckets={props.onManageBuckets}
                      />
                    );
                  }}
                </For>
              </Show>
            </div>
          </div>
        </Match>
      </Switch>
    </main>
  );
};

// --- Bucket grid presenter ---

export const BucketGrid: Component<{
  buckets: BucketEntry[];
  currency: string;
  /** US-INTEREST-02: needed by per-tile interest history disclosure. */
  sessionId: string;
  /** If provided, spendable buckets get a Spend button. Child-only. */
  onSpend?: (bucket: BucketEntry) => void;
  /** If provided, approval-required buckets get a Request button. Child-only. */
  onRequestWithdrawal?: (bucket: BucketEntry) => void;
  /** If provided, eligible bucket tiles get an Edit button. The
   *  `editPolicy` selects which buckets are eligible. */
  onEditBucket?: (bucket: BucketEntry, siblings: BucketEntry[]) => void;
  /** Edit eligibility policy. Defaults to `guardian-all`.
   *   - `guardian-all`: every bucket shows Edit, including child-managed
   *     ones (the guardian view, US-BUCKET-13). Guardians now have full
   *     edit authority on every bucket they can see -- partially supersedes
   *     US-BUCKET-06's "guardian view is read-only on child-managed
   *     buckets" acceptance criterion.
   *   - `child-self`: only buckets with `!isGuardianManaged` show Edit
   *     (the child editing their own buckets, US-BUCKET-08).
   */
  editPolicy?: 'guardian-all' | 'child-self';
  /** If provided, every bucket tile gets a Deactivate button (US-BUCKET-13).
   *  Children also get this affordance per US-BUCKET-14, gated by
   *  `deactivatePolicy` to their own child-managed buckets only. */
  onDeactivateBucket?: (bucket: BucketEntry, siblings: BucketEntry[]) => void;
  /** Deactivate eligibility policy. Mirrors `editPolicy`. Defaults to
   *  `guardian-all`.
   *   - `guardian-all`: every bucket shows Deactivate (the guardian
   *     view, US-BUCKET-13).
   *   - `child-self`: only buckets with `!isGuardianManaged` show
   *     Deactivate (the child view, US-BUCKET-14). The bucket list
   *     endpoint already scopes to the signed-in child, so an
   *     `b.childAccountId === self` check would be redundant --
   *     guardian-managed-ness is the only relevant filter.
   */
  deactivatePolicy?: 'guardian-all' | 'child-self';
  /** US-BUCKET-12 -- child-only "Delete" affordance. When provided,
   *  buckets the child is allowed to delete (not guardian-managed,
   *  not fixed-percent) get a Delete button that opens the
   *  delete-with-target form. Guardians don't see this -- they have
   *  Deactivate instead, which has different semantics (rebalance
   *  the percentages of the remaining buckets vs move-to-target). */
  onDeleteBucket?: (bucket: BucketEntry, siblings: BucketEntry[]) => void;
}> = (props) => {
  const displayPercents = createMemo(() => calcDisplayPercents(props.buckets));
  return (
    <ul class="bucket-grid">
      <For each={props.buckets}>
        {(b) => (
          <li class="bucket-tile" style={b.displayColor ? `--bucket-color: ${b.displayColor}` : ''}>
            <div class="bucket-tile-inner">
              <div class="bucket-tile-name">{b.bucketName}</div>
              <div class="bucket-tile-balance">
                {formatBalance(b.balanceMinorUnits, props.currency)}
              </div>
              <div class="bucket-tile-meta">
                <span class="bucket-tile-pct">{displayPercents()[b.bucketId] ?? 0}%</span>
                <Show when={b.percentIsFixed}>
                  <AppIcon
                    name="lock"
                    class="bucket-tile-flag"
                    title="Fixed percentage -- locked; won't shrink when more buckets are added"
                    data-testid={`bucket-fixed-${b.bucketId}`}
                  />
                </Show>
                <Show when={b.requiresApprovalToWithdraw}>
                  <AppIcon
                    name="approvals"
                    class="bucket-tile-flag"
                    title="Requires guardian approval to withdraw"
                  />
                </Show>
                <Show when={b.isGuardianManaged}>
                  <AppIcon name="person" class="bucket-tile-flag" title="Guardian-managed" />
                </Show>
              </div>
              <div class="bucket-tile-actions">
                <Show
                  when={props.onSpend && !b.requiresApprovalToWithdraw && b.balanceMinorUnits > 0}
                >
                  <button
                    type="button"
                    class="bucket-tile-spend"
                    onClick={() => props.onSpend?.(b)}
                  >
                    Spend
                  </button>
                </Show>
                <Show
                  when={
                    props.onRequestWithdrawal &&
                    b.requiresApprovalToWithdraw &&
                    b.balanceMinorUnits > 0
                  }
                >
                  <button
                    type="button"
                    class="bucket-tile-spend"
                    onClick={() => props.onRequestWithdrawal?.(b)}
                  >
                    Request
                  </button>
                </Show>
                <Show
                  when={
                    props.onEditBucket &&
                    ((props.editPolicy ?? 'guardian-all') === 'child-self'
                      ? !b.isGuardianManaged
                      : true)
                  }
                >
                  <button
                    type="button"
                    class="bucket-tile-edit"
                    onClick={() => props.onEditBucket?.(b, props.buckets.slice())}
                    data-testid={`bucket-edit-${b.bucketId}`}
                  >
                    Edit
                  </button>
                </Show>
                <Show
                  when={
                    props.onDeactivateBucket &&
                    ((props.deactivatePolicy ?? 'guardian-all') === 'child-self'
                      ? !b.isGuardianManaged
                      : true)
                  }
                >
                  <button
                    type="button"
                    class="bucket-tile-edit"
                    onClick={() => props.onDeactivateBucket?.(b, props.buckets.slice())}
                    data-testid={`bucket-deactivate-${b.bucketId}`}
                  >
                    Deactivate
                  </button>
                </Show>
                {/* US-BUCKET-12 -- child-only Delete with transfer target.
              Guardian-managed and fixed-percent buckets are off-limits
              per AC #1; the backend would reject those with Validation,
              but we hide the button up-front to prevent the friction. */}
                <Show when={props.onDeleteBucket && !b.isGuardianManaged && !b.percentIsFixed}>
                  <button
                    type="button"
                    class="bucket-tile-edit"
                    onClick={() => props.onDeleteBucket?.(b, props.buckets.slice())}
                    data-testid={`bucket-delete-${b.bucketId}`}
                  >
                    Delete
                  </button>
                </Show>
                {/* US-INTEREST-02 -- collapsible interest history per
              interest-enabled bucket. Closed by default to keep
              Home compact; lazy-loads on first open. The chip
              alongside the rate makes the credited-vs-display
              distinction visible at a glance: "Earning interest"
              for actually-credits buckets, "Earning interest
              (display only)" for educational ones. */}
                <Show when={b.interestEnabled}>
                  <BucketInterestSection
                    bucket={b}
                    currency={props.currency}
                    sessionId={props.sessionId}
                  />
                </Show>
              </div>
              {/* bucket-tile-actions */}
            </div>
            {/* bucket-tile-inner */}
          </li>
        )}
      </For>
    </ul>
  );
};

// US-ROUTINE-03 -- Today's routine tasks strip shown above bucket grid on child home.
export const TodaysRoutineTasks: Component<{
  sessionId: string;
  currency: string;
  onOpenTask: (instanceId: number) => void;
  onProgressChange?: (completed: number, total: number) => void;
}> = (props) => {
  const [instances, { refetch }] = createResource(
    () => props.sessionId,
    (sid) => routineInstancesToday(sid).then((r) => (r.ok ? r.value : [])),
  );
  const [resubmittingId, setResubmittingId] = createSignal<number | null>(null);
  const [resubmitError, setResubmitError] = createSignal<string | null>(null);

  const localDateString = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const canResubmit = (instance: RoutineInstanceEntry) =>
    instance.status === 'rejected' && instance.scheduledDate === localDateString();

  const resubmit = async (instance: RoutineInstanceEntry) => {
    if (!canResubmit(instance) || resubmittingId() !== null) return;
    setResubmittingId(instance.instanceId);
    setResubmitError(null);
    const result = await routineResubmit(props.sessionId, instance.instanceId);
    setResubmittingId(null);
    if (!result.ok) {
      setResubmitError(result.error.message);
      return;
    }
    await refetch();
  };

  // A routine can become visible while the child keeps the dashboard open.
  // Refresh once a minute so visibility/deadline changes do not require a
  // logout or manual reload.
  const refreshTimer = window.setInterval(async () => {
    await routinesGenerateToday(props.sessionId);
    await refetch();
  }, 60_000);
  onCleanup(() => window.clearInterval(refreshTimer));

  // A guardian decision can arrive while the child is already on Home. The
  // shell raises this event only after a cloud snapshot has been applied, so
  // refresh the result instead of leaving an approved/rejected routine looking
  // as though it is still waiting until the next timer tick.
  const onCloudSyncApplied = () => void refetch();
  window.addEventListener('chorepop:sync-applied', onCloudSyncApplied);
  onCleanup(() => window.removeEventListener('chorepop:sync-applied', onCloudSyncApplied));

  const pending = () =>
    (instances() ?? []).filter(
      (i) =>
        (i.status === 'pending_child' || i.status === 'pending_guardian') &&
        !isFamilyContentHidden(`routine-instance:${i.instanceId}`),
    );

  // Keep today's resolved routine visible long enough for the child to
  // understand the guardian's decision. This is deliberately separate from
  // money approval notices: a zero-reward routine still needs an outcome and
  // a routine rejection must never be presented as a withdrawal.
  const resolved = () =>
    (instances() ?? []).filter(
      (i) =>
        (i.status === 'approved' || i.status === 'rejected') &&
        !isFamilyContentHidden(`routine-instance:${i.instanceId}`),
    );

  createEffect(() => {
    const today = instances() ?? [];
    const completed = today.filter(
      (instance) => instance.status === 'pending_guardian' || instance.status === 'approved',
    ).length;
    props.onProgressChange?.(completed, today.length);
  });

  const outcomeLabel = (instance: RoutineInstanceEntry) =>
    instance.status === 'approved' ? 'Routine approved' : 'Routine not approved';

  return (
    <Show when={pending().length > 0 || resolved().length > 0}>
      <section class="routine-today-section" data-testid="routine-today-section">
        <h2 class="routine-today-heading">Today's tasks</h2>
        <Show when={resubmitError()}>
          <div class="form-error" role="alert">
            {resubmitError()}
          </div>
        </Show>
        <ul class="routine-today-list">
          <For each={pending()}>
            {(inst: RoutineInstanceEntry) => (
              <li
                class="routine-today-item"
                classList={{ 'routine-today-item--waiting': inst.status === 'pending_guardian' }}
              >
                <button
                  type="button"
                  class="routine-today-open"
                  disabled={inst.status !== 'pending_child'}
                  onClick={() => props.onOpenTask(inst.instanceId)}
                  data-testid={`routine-today-${inst.instanceId}`}
                >
                  <span class="routine-today-name">{inst.taskName}</span>
                  <span class="routine-today-deadline">by {inst.deadlineTime}</span>
                  <Show when={inst.status === 'pending_guardian'}>
                    <span class="routine-today-badge">Waiting</span>
                  </Show>
                  <Show when={inst.subTasks.length > 0}>
                    <span class="routine-today-steps">
                      {inst.tickedSubTaskIds.length}/{inst.subTasks.length} steps
                    </span>
                  </Show>
                </button>
                <ReportContentButton
                  context={{
                    contentKind: 'routine',
                    contentLocalId: `routine-instance:${inst.instanceId}`,
                    label: `routine “${inst.taskName}”`,
                    contentSnapshot: {
                      instanceId: inst.instanceId,
                      recurringTaskId: inst.recurringTaskId,
                      taskName: inst.taskName,
                      childAccountId: inst.childAccountId,
                      childDisplayName: inst.childDisplayName,
                      scheduledDate: inst.scheduledDate,
                      rejectionReason: inst.rejectionReason,
                      steps: inst.subTasks.map((step) => ({
                        subTaskId: step.subTaskId,
                        sortOrder: step.sortOrder,
                        subTaskName: step.subTaskName,
                      })),
                    },
                  }}
                />
              </li>
            )}
          </For>
          <For each={resolved()}>
            {(inst: RoutineInstanceEntry) => (
              <li
                class="routine-today-item routine-today-item--resolved"
                classList={{
                  'routine-today-item--approved': inst.status === 'approved',
                  'routine-today-item--rejected': inst.status === 'rejected',
                }}
                data-testid={`routine-outcome-${inst.instanceId}`}
              >
                <span class="routine-today-name">{inst.taskName}</span>
                <span class="routine-today-badge">{outcomeLabel(inst)}</span>
                <Show when={inst.status === 'approved' && inst.rewardMinorUnits > 0}>
                  <span class="routine-today-outcome-reward">
                    {formatBalance(inst.rewardMinorUnits, props.currency)} added
                  </span>
                </Show>
                <Show when={inst.status === 'rejected' && inst.rejectionReason}>
                  <span class="routine-today-outcome-reason">{inst.rejectionReason}</span>
                </Show>
                <Show when={canResubmit(inst)}>
                  <button
                    type="button"
                    class="routine-text-button"
                    disabled={resubmittingId() !== null}
                    onClick={() => void resubmit(inst)}
                    data-testid={`routine-resubmit-${inst.instanceId}`}
                  >
                    {resubmittingId() === inst.instanceId ? 'Resubmitting…' : 'Resubmit'}
                  </button>
                </Show>
                <ReportContentButton
                  context={{
                    contentKind: 'routine',
                    contentLocalId: `routine-instance:${inst.instanceId}`,
                    label: `routine result “${inst.taskName}”`,
                    contentSnapshot: {
                      instanceId: inst.instanceId,
                      recurringTaskId: inst.recurringTaskId,
                      taskName: inst.taskName,
                      childAccountId: inst.childAccountId,
                      childDisplayName: inst.childDisplayName,
                      scheduledDate: inst.scheduledDate,
                      status: inst.status,
                      rejectionReason: inst.rejectionReason,
                    },
                  }}
                />
              </li>
            )}
          </For>
        </ul>
      </section>
    </Show>
  );
};

// US-INTEREST-02 -- per-bucket interest history. Lives on the
// bucket tile under all the action buttons. Closed by default;
// fetches the recent-accruals list lazily on first open and caches
// the result for the lifetime of the tile.
const BucketInterestSection: Component<{
  bucket: BucketEntry;
  currency: string;
  sessionId: string;
}> = (props) => {
  const [open, setOpen] = createSignal(false);
  const [accruals, setAccruals] = createSignal<BucketInterestAccrualEntry[] | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

  const ensureLoaded = async () => {
    if (accruals() !== null || loading()) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const r = await bucketInterestAccrualsList({
        sessionId: props.sessionId,
        bucketId: props.bucket.bucketId,
      });
      if (!r.ok) {
        setErrorMsg(r.error.message);
        return;
      }
      setAccruals(r.value);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    const next = !open();
    setOpen(next);
    if (next) void ensureLoaded();
  };

  // Per-bucket header chip: distinct for actually-credits vs
  // display-only so even with the section closed, the child can
  // see at a glance which mode is active.
  const headerLabel = () => {
    const ratePct = (props.bucket.interestRateBps / 100).toFixed(2);
    const period = props.bucket.interestPeriodCode ?? 'period';
    if (props.bucket.interestActuallyCredits) {
      return `Earning interest · ${ratePct}% per ${period}`;
    }
    return `Earning interest (display only) · ${ratePct}% per ${period}`;
  };

  return (
    <div
      class={
        props.bucket.interestActuallyCredits
          ? 'bucket-interest-section bucket-interest-section--credited'
          : 'bucket-interest-section bucket-interest-section--display-only'
      }
      data-testid={`bucket-interest-section-${props.bucket.bucketId}`}
    >
      <button
        type="button"
        class="bucket-interest-toggle"
        onClick={toggle}
        aria-expanded={open()}
        data-testid={`bucket-interest-toggle-${props.bucket.bucketId}`}
      >
        <span class="bucket-interest-label">{headerLabel()}</span>
        <span class="bucket-interest-chevron" aria-hidden="true">
          {open() ? '▾' : '▸'}
        </span>
      </button>
      <Show when={open()}>
        <div class="bucket-interest-body">
          <Show when={loading()}>
            <p class="bucket-interest-empty">Loading…</p>
          </Show>
          <Show when={errorMsg()}>
            {(msg) => (
              <p class="form-error" role="alert">
                {msg()}
              </p>
            )}
          </Show>
          <Show when={!loading() && !errorMsg() && accruals()?.length === 0}>
            <p class="bucket-interest-empty">No interest yet.</p>
          </Show>
          <Show when={!loading() && (accruals()?.length ?? 0) > 0}>
            <ul class="bucket-interest-list">
              <For each={accruals() ?? []}>
                {(a) => (
                  <li
                    class={
                      a.wasCredited
                        ? 'bucket-interest-row bucket-interest-row--credited'
                        : 'bucket-interest-row bucket-interest-row--display-only'
                    }
                  >
                    <div class="bucket-interest-row-amount">
                      +{formatBalance(a.interestMinorUnits, props.currency)}
                    </div>
                    <div class="bucket-interest-row-meta">
                      <span>{formatAccrualDate(a.periodEndAtUtc)}</span>
                      <span>·</span>
                      <span>
                        {(a.rateBpsSnapshot / 100).toFixed(2)}% on{' '}
                        {formatBalance(a.principalMinorUnits, props.currency)}
                      </span>
                    </div>
                    <span
                      class={
                        a.wasCredited
                          ? 'bucket-interest-badge bucket-interest-badge--credited'
                          : 'bucket-interest-badge bucket-interest-badge--display-only'
                      }
                      data-testid={`bucket-interest-row-${a.interestAccrualId}-badge`}
                    >
                      {a.wasCredited ? 'Credited to balance' : 'Display only (educational)'}
                    </span>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </div>
      </Show>
    </div>
  );
};

function formatAccrualDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatShortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatBalance(minorUnits: number, currencyCode: string): string {
  const major = minorUnits / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(major);
  } catch {
    return `${currencyCode} ${major.toFixed(2)}`;
  }
}
